import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePayerWizardContext } from '../PayerWizardContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ArrowLeft, 
  CheckCircle, 
  AlertTriangle, 
  Building2, 
  Layers, 
  Route,
  Zap,
  Loader2,
} from 'lucide-react';
import { PAYER_TYPES, US_STATES, RULESET_SOURCES, LANE_DISPLAY } from '@/lib/payer-wizard/types';
import { SetupCompletionChecklist } from '../SetupCompletionChecklist';
import { TestDrivePanel } from '../TestDrivePanel';
import { generateChecklist } from '@/lib/payer-wizard/checklistUtils';
import { toast } from 'sonner';

export function PayerStep5Activate() {
  const navigate = useNavigate();
  const { 
    progress, 
    ruleReview,
    goToStep,
    canActivate,
    activatePayer,
    loading,
    error,
  } = usePayerWizardContext();
  
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);
  
  if (!progress) return null;
  
  const checklist = generateChecklist(progress, ruleReview);
  const { allowed, reasons } = canActivate();
  
  const handleActivate = async () => {
    setActivating(true);
    const success = await activatePayer();
    setActivating(false);
    
    if (success) {
      setActivated(true);
      toast.success('Payer activated successfully!', {
        description: `${progress.payer_name} is now ready for use.`,
      });
    } else {
      toast.error('Activation failed', {
        description: error || 'Please try again.',
      });
    }
  };
  
  if (activated) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="p-4 rounded-full bg-primary/10">
              <CheckCircle className="h-12 w-12 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Payer Activated!</h2>
            <p className="text-muted-foreground max-w-md">
              {progress.payer_name} has been successfully configured with {progress.plans.length} plan(s).
              Cases can now be created using this payer.
            </p>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => navigate('/admin/payer-plans')}>
                View Payer List
              </Button>
              <Button variant="outline" onClick={() => navigate('/rules')}>
                Manage Rules
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Step 5: Review & Activate
        </CardTitle>
        <CardDescription>
          Review your configuration and activate the payer
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Completion Checklist */}
        <SetupCompletionChecklist items={checklist.items} />
        
        {/* Summary Sections */}
        <div className="grid gap-4">
          {/* Payer Info */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold">Payer Information</h3>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Name:</span>{' '}
                <span className="font-medium">{progress.payer_name}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Type:</span>{' '}
                <Badge variant="outline">
                  {PAYER_TYPES.find(t => t.value === progress.payer_type)?.label}
                </Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Jurisdiction:</span>{' '}
                <span>{US_STATES.find(s => s.value === progress.jurisdiction)?.label}</span>
              </div>
            </div>
          </div>
          
          {/* Plans with Lane Config */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold">Plans ({progress.plans.length})</h3>
            </div>
            <div className="space-y-3">
              {progress.plans.map((plan, i) => {
                const laneConfig = progress.plan_lane_configs[i];
                return (
                  <div key={i} className="border rounded p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{plan.plan_name}</span>
                        {plan.is_default && (
                          <Badge variant="secondary" className="text-xs">Default</Badge>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {RULESET_SOURCES.find(s => s.value === plan.ruleset_source)?.label}
                      </Badge>
                    </div>
                    
                    {laneConfig && (
                      <div className="flex items-center gap-2">
                        <Route className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Lanes:</span>
                        <div className="flex gap-1">
                          {laneConfig.lane_priority.map((lane, idx) => (
                            <Badge 
                              key={lane} 
                              variant="outline" 
                              className="text-xs flex items-center gap-1"
                            >
                              {idx + 1}. {LANE_DISPLAY[lane].label.split(' ')[0]}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Activation Status */}
        {!checklist.canActivate && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium mb-1">Cannot activate yet:</p>
              <ul className="list-disc list-inside text-sm">
                {checklist.blockerMessages.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        
        {checklist.canActivate && (
          <Alert className="border-primary/30 bg-primary/5">
            <CheckCircle className="h-4 w-4 text-primary" />
            <AlertDescription className="text-primary">
              All configuration steps are complete. You can now activate this payer.
            </AlertDescription>
          </Alert>
        )}
        
        {/* Test Drive Panel */}
        <TestDrivePanel progress={progress} ruleReview={ruleReview} />
        
        <Separator />
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => goToStep(4)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button 
            onClick={handleActivate} 
            disabled={!checklist.canActivate || activating || loading}
            className="min-w-[160px]"
          >
            {activating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Activating...
              </>
            ) : (
              <>
                <Zap className="mr-2 h-4 w-4" />
                Activate Payer
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
